import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';

const GAME_CATEGORIES = [
  'Icebreaking',
  'Language Game'
];

export default function GameAnalyticsPage() {
  const { user } = useUser();
  const [games, setGames] = useState([]);
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'past'
  const [selectedEventId, setSelectedEventId] = useState('all'); // 'all' for 전체, 특정 이벤트 ID
  const [feedbackData, setFeedbackData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // 권한 확인 - 모든 스태프 역할 허용
  if (!user?.isLoggedIn || !['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchData();
  }, []);

  // 탭이 변경될 때마다 데이터 다시 가져오기
  useEffect(() => {
    if (games.length > 0) {
      const eventsToUse = activeTab === 'past' ? pastEvents : events;
      fetchFeedbackData(eventsToUse);
      // 탭 변경 시 이벤트 선택 초기화
      setSelectedEventId('all');
    }
  }, [activeTab]);

  // 이벤트가 변경될 때마다 피드백 데이터 다시 계산
  useEffect(() => {
    if (feedbackData.length > 0) {
      // 데이터는 이미 로드되어 있으므로 리렌더링만 트리거
    }
  }, [selectedEventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // 게임, 현재 이벤트, 과거 이벤트 데이터를 병렬로 가져오기
      const [gamesResponse, eventsResponse, pastEventsResponse] = await Promise.all([
        axios.get('/api/admin/games'),
        axios.get('/api/admin/events'),
        axios.get('/api/feedback/past-events').catch(() => ({ data: [] })) // 과거 이벤트가 없을 수 있음
      ]);
      
      setGames(gamesResponse.data || []);
      setEvents(eventsResponse.data || []);
      setPastEvents(pastEventsResponse.data || []);
      
      // 현재 탭에 따라 피드백 데이터 가져오기
      const eventsToUse = activeTab === 'past' ? (pastEventsResponse.data || []) : (eventsResponse.data || []);
      await fetchFeedbackData(eventsToUse);
      
    } catch (error) {
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedbackData = async (eventsList) => {
    try {
      const allGameFeedbacks = [];
      
      for (const event of eventsList) {
        try {
          // 이벤트 통계 정보 가져오기 (실제 참여자 수)
          let actualAttendeeCount = event.attendeeCount || 0;
          try {
            const statisticsResponse = await axios.get(`/api/admin/events/${event.id}/statistics/pre-event`, {
              validateStatus: (status) => status < 500 // 4xx는 성공으로 처리하여 console.error 방지
            });
            if (statisticsResponse.status < 400 && statisticsResponse.data && !statisticsResponse.data.message) {
              actualAttendeeCount = statisticsResponse.data.currentAttendees || statisticsResponse.data.totalMembers || 0;
            } else {
              // Pre-event 스냅샷이 없으면 현재 통계 사용
              const currentStatsResponse = await axios.get(`/api/admin/events/${event.id}/statistics/current`, {
                validateStatus: (status) => status < 500
              });
              if (currentStatsResponse.status < 400) {
                actualAttendeeCount = currentStatsResponse.data.currentAttendees || currentStatsResponse.data.totalMembers || 0;
              }
            }
          } catch (error) {
            // 500대 에러만 여기서 처리 (4xx는 위에서 처리됨)
          }
          
          // 각 이벤트의 게임 피드백 데이터 가져오기
          const gameResponse = await axios.get(`/api/feedback/event/${event.id}/games`);
          const gameData = gameResponse.data || [];
          
          // 상세 피드백 (태그 포함) 가져오기
          const detailedFeedbacks = [];
          try {
            const detailResponse = await axios.get(`/api/feedback/event/${event.id}/details`);
            
            if (detailResponse.data && Array.isArray(detailResponse.data)) {
              detailResponse.data.forEach(feedback => {
                if (feedback.gameRatings && typeof feedback.gameRatings === 'object') {
                  Object.keys(feedback.gameRatings).forEach(gameId => {
                    const gameRating = feedback.gameRatings[gameId];
                    detailedFeedbacks.push({
                      gameId: gameId,
                      positiveOptions: gameRating.positiveOptions || [],
                      negativeOptions: gameRating.negativeOptions || [],
                      customFeedback: feedback.gameNotes && feedback.gameNotes[gameId] ? feedback.gameNotes[gameId] : ''
                    });
                  });
                }
              });
            }
          } catch (error) {
            // Silent fail
          }
          
          // 게임 데이터에 이벤트 정보와 상세 피드백 추가
          gameData.forEach(game => {
            // 해당 게임의 모든 상세 피드백 찾기
            const gameFeedbacks = detailedFeedbacks.filter(fb => {
              // gameId 매칭 로직 개선
              const fbGameId = fb.gameId?.toString() || '';
              const gameId = game.gameId?.toString() || '';
              
              // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
              if (fbGameId === gameId) return true;
              
              // game-01 -> 1 변환해서 비교
              const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
              const gameNumeric = gameId.replace('game-', '').replace(/^0+/, '') || '0';
              
              return fbNumeric === gameNumeric;
            });
            
            allGameFeedbacks.push({
              gameId: game.gameId,
              rating: game.rating,
              responseCount: game.responseCount,
              eventId: event.id,
              eventTitle: event.title,
              eventDate: event.eventDateTime,
              attendeeCount: actualAttendeeCount, // 실제 참여자 수 (통계 API에서 가져옴)
              willAttendCount: actualAttendeeCount, // 실제 참여자 수
              branch: event.branch || 'Unknown',
              positiveOptions: gameFeedbacks.flatMap(fb => fb.positiveOptions),
              negativeOptions: gameFeedbacks.flatMap(fb => fb.negativeOptions),
              customFeedbacks: gameFeedbacks.map(fb => fb.customFeedback).filter(f => f)
            });
          });
        } catch (error) {
          // 게임 피드백이 없는 이벤트는 무시
        }
      }
      
      setFeedbackData(allGameFeedbacks);
    } catch (error) {
      // Silent fail
    }
  };

  // 필터링된 피드백 데이터 가져오기
  const getFilteredFeedbackData = () => {
    if (selectedEventId === 'all') {
      return feedbackData;
    }
    return feedbackData.filter(fb => fb.eventId === parseInt(selectedEventId));
  };

  // 게임별 플레이 횟수 계산
  const calculateGamePlayCount = (gameId) => {
    const filteredData = getFilteredFeedbackData();
    const count = filteredData.filter(feedback => {
      if (!feedback.gameId) return false;
      
      const gameIdStr = gameId.toString();
      const feedbackGameIdStr = feedback.gameId.toString();
      
      // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
      if (feedbackGameIdStr === gameIdStr) return true;
      
      // game-01 -> 1 변환해서 비교
      const feedbackNumeric = feedbackGameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
      const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
      
      return feedbackNumeric === gameNumeric;
    }).length;
    
    return count;
  };

  // 게임별 평균 평점 계산
  const calculateGameAverageRating = (gameId) => {
    const filteredData = getFilteredFeedbackData();
    const gameFeedbacks = filteredData.filter(feedback => {
      if (!feedback.gameId || !feedback.rating) return false;
      
      const gameIdStr = gameId.toString();
      const feedbackGameIdStr = feedback.gameId.toString();
      
      // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
      if (feedbackGameIdStr === gameIdStr) return true;
      
      // game-01 -> 1 변환해서 비교
      const feedbackNumeric = feedbackGameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
      const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
      
      return feedbackNumeric === gameNumeric;
    });
    
    if (gameFeedbacks.length === 0) return 0;
    
    const totalRating = gameFeedbacks.reduce((sum, feedback) => sum + parseFloat(feedback.rating), 0);
    const averageRating = totalRating / gameFeedbacks.length;
    
    return averageRating.toFixed(1);
  };

  // 통계 계산
  const getStats = () => {
    const filteredData = getFilteredFeedbackData();
    
    const gamesWithStats = games.map(game => {
      const gameIdToUse = game.gameId || game.id?.toString();
      const playedCount = calculateGamePlayCount(gameIdToUse);
      const averageRating = calculateGameAverageRating(gameIdToUse);
      return { ...game, playedCount, averageRating };
    });

    const totalGames = gamesWithStats.length;
    const totalFeedback = gamesWithStats.reduce((sum, game) => sum + (game.playedCount || 0), 0);
    const gamesWithRating = gamesWithStats.filter(game => game.averageRating && game.averageRating > 0);
    const avgRating = gamesWithRating.length > 0 
      ? gamesWithRating.reduce((sum, game) => sum + parseFloat(game.averageRating), 0) / gamesWithRating.length 
      : 0;

    return { totalGames, avgRating: avgRating.toFixed(1), totalFeedback, gamesWithStats };
  };

  const stats = getStats();

  // 카테고리 접기/펴기 토글
  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🎯 Game Analytics</h1>
          <p className="text-gray-600 text-sm sm:text-base">Performance analysis and insights for your games</p>
        </div>

        {/* 탭 메뉴 */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex gap-6">
            <button 
              onClick={() => setActiveTab('current')} 
              className={`py-3 px-3 border-b-2 font-medium rounded-t-lg transition-all duration-200 ${
                activeTab === 'current' 
                  ? 'border-blue-500 text-blue-600 bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-25'
              }`}
            >
              📋 Current Events
            </button>
            <button 
              onClick={() => setActiveTab('past')} 
              className={`py-3 px-3 border-b-2 font-medium rounded-t-lg transition-all duration-200 ${
                activeTab === 'past' 
                  ? 'border-blue-500 text-blue-600 bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-25'
              }`}
            >
              📚 Past Feedback
            </button>
          </nav>
        </div>

        {/* 이벤트 필터 */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📅 Event Filter
              </label>
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="w-full max-w-md p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">📊 All Events (Overall Trends)</option>
                {(activeTab === 'past' ? pastEvents : events).map(event => (
                  <option key={event.id} value={event.id}>
                    {event.title} - {new Date(event.eventDateTime).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="text-sm text-gray-600">
              {selectedEventId === 'all' 
                ? `Showing data from ${(activeTab === 'past' ? pastEvents : events).length} ${activeTab === 'past' ? 'past' : 'current'} events`
                : `Showing data from: ${(activeTab === 'past' ? pastEvents : events).find(e => e.id === parseInt(selectedEventId))?.title || 'Selected Event'}`
              }
            </div>
          </div>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Games</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalGames}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Average Rating</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.avgRating}</p>
              </div>
              </div>
            </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Feedback</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalFeedback}</p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading analytics...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* 1. Top 5 Popular Games */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">🏆 Top 5 Popular Games</h3>
              <p className="text-sm text-gray-600 mb-4">Most validated games based on popularity score (Rating × Total Votes)</p>
              <div className="space-y-3">
                {(() => {
                  const gamesWithPopularity = stats.gamesWithStats
                    .map(game => {
                      const rating = parseFloat(game.averageRating) || 0;
                      const votes = game.playedCount || 0;
                      const popularityScore = rating * votes;
                      return { ...game, popularityScore, rating, votes };
                    })
                    .filter(game => game.popularityScore > 0)
                    .sort((a, b) => b.popularityScore - a.popularityScore)
                    .slice(0, 5);

                  if (gamesWithPopularity.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        No game feedback data available yet
                      </div>
                    );
                  }

                  return gamesWithPopularity.map((game, index) => (
                    <div key={game.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-4 flex-1">
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold ${
                          index === 0 ? 'bg-yellow-400 text-yellow-900 shadow-lg' :
                          index === 1 ? 'bg-gray-300 text-gray-700' :
                          index === 2 ? 'bg-orange-400 text-orange-900' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{game.name}</div>
                          <div className="text-sm text-gray-500">{game.category}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{game.popularityScore.toFixed(1)}</div>
                          <div className="text-xs text-gray-500">Popularity Score</div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center">
                            <span className="text-lg font-semibold text-gray-900">{game.rating.toFixed(1)}</span>
                            <span className="text-yellow-400 ml-1">★</span>
                          </div>
                          <div className="text-xs text-gray-500">{game.votes} votes</div>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
              </div>

            {/* 2. Overall Feedback Trends - AdminFeedbackPage 스타일 */}
              <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">🏷️ Overall Feedback Trends</h3>
              <p className="text-sm text-gray-600 mb-4">
                {selectedEventId === 'all' 
                  ? 'Aggregated feedback across all events - Systemic strengths and weaknesses'
                  : `Feedback analysis for selected event`
                }
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Positive Feedback */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                    <span className="text-xl mr-2">👍</span>
                    Positive Feedback
                  </h4>
                  <div className="space-y-3">
                    {['Fun & engaging', 'Helped me connect', 'Right duration'].map(tag => {
                      const filteredData = getFilteredFeedbackData();
                      const tagCount = filteredData.filter(fb => 
                        fb.positiveOptions && fb.positiveOptions.includes(tag)
                      ).length;
                      
                      const totalFeedbacks = filteredData.length;
                      const percentage = totalFeedbacks > 0 ? (tagCount / totalFeedbacks) * 100 : 0;
                      
                      return (
                        <div key={tag} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-green-800 font-medium">{tag}</span>
                            <span className="text-green-600">{tagCount} ({percentage.toFixed(0)}%)</span>
                          </div>
                          <div className="w-full bg-green-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Top 3 games for "Helped me connect" */}
                  <div className="mt-4 pt-4 border-t border-green-300">
                    <h5 className="text-sm font-semibold text-green-800 mb-2">🌟 Best Social Games</h5>
                    <div className="space-y-1">
                      {(() => {
                        const socialGames = stats.gamesWithStats.map(game => {
                          const gameIdStr = game.id.toString();
                          const connectCount = getFilteredFeedbackData().filter(fb => {
                            if (!fb.gameId) return false;
                            
                            const fbGameId = fb.gameId.toString();
                            
                            // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                            if (fbGameId === gameIdStr) {
                              return fb.positiveOptions && fb.positiveOptions.includes('Helped me connect');
                            }
                            
                            // game-01 -> 1 변환해서 비교
                            const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                            const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                            
                            return fbNumeric === gameNumeric && fb.positiveOptions && fb.positiveOptions.includes('Helped me connect');
                          }).length;
                          return { ...game, connectCount };
                        })
                        .filter(game => game.connectCount > 0)
                        .sort((a, b) => b.connectCount - a.connectCount)
                        .slice(0, 3);

                        if (socialGames.length === 0) {
                          return <div className="text-xs text-gray-500 italic">No data yet</div>;
                        }

                        return socialGames.map((game, idx) => (
                          <div key={game.id} className="flex items-center justify-between text-xs p-1 bg-white rounded">
                            <span className="text-gray-900">{idx + 1}. {game.name}</span>
                            <span className="text-green-600 font-bold">{game.connectCount}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>

                {/* Negative Feedback */}
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                    <span className="text-xl mr-2">👎</span>
                    Negative Feedback
                  </h4>
                  <div className="space-y-3">
                    {['Boring', 'Group size issue (too many/few)', 'Confusing / unclear'].map(tag => {
                      const filteredData = getFilteredFeedbackData();
                      const tagCount = filteredData.filter(fb => 
                        fb.negativeOptions && fb.negativeOptions.includes(tag)
                      ).length;
                      
                      const totalFeedbacks = filteredData.length;
                      const percentage = totalFeedbacks > 0 ? (tagCount / totalFeedbacks) * 100 : 0;
                      
                      return (
                        <div key={tag} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-red-800 font-medium">{tag}</span>
                            <span className="text-red-600">{tagCount} ({percentage.toFixed(0)}%)</span>
                          </div>
                          <div className="w-full bg-red-200 rounded-full h-2">
                            <div 
                              className="bg-red-600 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Games needing improvement */}
                  <div className="mt-4 pt-4 border-t border-red-300">
                    <h5 className="text-sm font-semibold text-red-800 mb-2">⚠️ Needs Attention</h5>
                    <div className="space-y-2">
                      {(() => {
                        const filteredData = getFilteredFeedbackData();
                        
                        // Confusing games
                        const confusingGames = stats.gamesWithStats.map(game => {
                          const gameIdStr = game.id.toString();
                          const count = filteredData.filter(fb => {
                            if (!fb.gameId) return false;
                            
                            const fbGameId = fb.gameId.toString();
                            
                            // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                            if (fbGameId === gameIdStr) {
                              return fb.negativeOptions && fb.negativeOptions.includes('Confusing / unclear');
                            }
                            
                            // game-01 -> 1 변환해서 비교
                            const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                            const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                            
                            return fbNumeric === gameNumeric && fb.negativeOptions && fb.negativeOptions.includes('Confusing / unclear');
                          }).length;
                          return { ...game, issueCount: count, issueType: 'Confusing' };
                        });
                        
                        // Group size issue games
                        const sizeIssueGames = stats.gamesWithStats.map(game => {
                          const gameIdStr = game.id.toString();
                          const count = filteredData.filter(fb => {
                            if (!fb.gameId) return false;
                            
                            const fbGameId = fb.gameId.toString();
                            
                            // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                            if (fbGameId === gameIdStr) {
                              return fb.negativeOptions && fb.negativeOptions.includes('Group size issue (too many/few)');
                            }
                            
                            // game-01 -> 1 변환해서 비교
                            const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                            const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                            
                            return fbNumeric === gameNumeric && fb.negativeOptions && fb.negativeOptions.includes('Group size issue (too many/few)');
                          }).length;
                          return { ...game, issueCount: count, issueType: 'Size Issue' };
                        });
                        
                        const allIssues = [...confusingGames, ...sizeIssueGames]
                          .filter(game => game.issueCount > 0)
                          .sort((a, b) => b.issueCount - a.issueCount)
                          .slice(0, 3);

                        if (allIssues.length === 0) {
                          return <div className="text-xs text-gray-500 italic">No issues reported! 🎉</div>;
                        }

                        return allIssues.map((game, idx) => (
                          <div key={`${game.id}-${game.issueType}`} className="text-xs p-1 bg-white rounded">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-900 font-medium">{game.name}</span>
                              <span className="text-red-600 font-bold">{game.issueCount}</span>
                            </div>
                            <div className="text-red-700 text-xs">Issue: {game.issueType}</div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Best Games by Event Size - 총 이벤트 참여 인원 기준 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">👥 Best Games by Event Size</h3>
              <p className="text-sm text-gray-600 mb-4">
                Game performance based on <strong>total event attendance</strong>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { label: 'Small Event (~40 people)', min: 1, max: 40, emoji: '👥' },
                  { label: 'Medium Event (40-60 people)', min: 40, max: 60, emoji: '👨‍👩‍👧‍👦' },
                  { label: 'Large Event (60+ people)', min: 60, max: 999, emoji: '🎪' }
                ].map(group => {
                  // 총 이벤트 참여 인원 기준으로 필터링
                  const gamesInGroup = stats.gamesWithStats
                    .map(game => {
                      const gameIdStr = (game.gameId || game.id?.toString()).toString();
                      
                      // 해당 게임의 피드백 데이터에서 이벤트 크기별 평점 계산
                      const eventSizeFeedbacks = getFilteredFeedbackData().filter(fb => {
                        if (!fb.gameId) return false;
                        
                        const fbGameId = fb.gameId.toString();
                        
                        // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                        if (fbGameId === gameIdStr) return true;
                        
                        const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                        const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                        
                        return fbNumeric === gameNumeric;
                      }).filter(fb => {
                        // 이벤트 총 참여 인원이 해당 그룹 범위에 맞는지 확인
                        const totalAttendance = fb.willAttendCount || fb.attendeeCount || 0;
                        return totalAttendance >= group.min && totalAttendance <= group.max;
                      });
                      
                      if (eventSizeFeedbacks.length === 0) {
                        return null; // 해당 이벤트 크기에서 플레이되지 않은 게임
                      }
                      
                      // 해당 이벤트 크기에서의 평균 평점 계산
                      const totalRating = eventSizeFeedbacks.reduce((sum, fb) => sum + parseFloat(fb.rating || 0), 0);
                      const avgRating = totalRating / eventSizeFeedbacks.length;
                      const playCount = eventSizeFeedbacks.length;
                      
                      return { 
                        ...game, 
                        avgRating, 
                        playCount,
                        eventSizeFeedbacks,
                        // 인기 점수로 정렬 (평점이 높고 플레이 횟수도 많은 게임)
                        popularityScore: avgRating * Math.log(playCount + 1)
                      };
                    })
                    .filter(game => game !== null && game.playCount > 0) // 데이터가 있는 게임만
                    .sort((a, b) => {
                      // 먼저 평점으로 정렬, 같으면 플레이 횟수로 정렬
                      if (Math.abs(b.avgRating - a.avgRating) > 0.1) {
                        return b.avgRating - a.avgRating;
                      }
                      return b.playCount - a.playCount;
                    })
                    .slice(0, 5); // Top 5로 확장

                  // 해당 이벤트 크기에서 플레이된 게임 수 계산
                  const totalGamesInThisSize = getFilteredFeedbackData().filter(fb => {
                    const totalAttendance = fb.willAttendCount || fb.attendeeCount || 0;
                    return totalAttendance >= group.min && totalAttendance <= group.max;
                  }).length;
                   
                  return (
                    <div key={group.label} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <span className="text-xl mr-2">{group.emoji}</span>
                        {group.label}
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">{totalGamesInThisSize} events in this size</p>
                      
                      <div className="space-y-2">
                        {gamesInGroup.length === 0 ? (
                          <div className="text-sm text-gray-500 italic py-4 text-center">
                            No games played in {group.label.toLowerCase()} yet
                          </div>
                        ) : (
                          gamesInGroup.map((game, idx) => (
                            <div key={game.id} className="p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`} {game.name}
                                </span>
                                {game.avgRating > 0 && (
                                  <div className="flex items-center">
                                    <span className="text-sm font-bold text-blue-600">{game.avgRating.toFixed(1)}</span>
                                    <span className="text-yellow-400 ml-1">★</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>
                                  Avg Rating: {game.avgRating.toFixed(1)}
                                </span>
                                {game.playCount > 0 && (
                                  <span className="text-blue-600">
                                    {game.playCount} play{game.playCount > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 4. Category Performance - Detailed View */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">📊 Category Performance Detail</h3>
              <p className="text-sm text-gray-600 mb-4">Detailed play history and feedback by category</p>
              
              <div className="space-y-6">
                {GAME_CATEGORIES.map(category => {
                  const categoryGames = stats.gamesWithStats.filter(game => game.category === category);
                    
                  return (
                    <div key={category} className="border border-gray-200 rounded-lg p-6 bg-gradient-to-r from-gray-50 to-white">
                      <div 
                        className="flex items-center justify-between mb-4 cursor-pointer hover:bg-gray-100 -m-6 p-4 rounded-t-lg transition-colors"
                        onClick={() => toggleCategory(category)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg transition-transform duration-200" style={{ transform: collapsedCategories[category] ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                            {category === 'Icebreaking' ? '🧊' : '🗣️'}
                          </span>
                          <h4 className="font-semibold text-gray-900 text-base">{category}</h4>
                        </div>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {categoryGames.length} games
                        </span>
                      </div>
                      
                      {!collapsedCategories[category] && (
                        <div className="space-y-4">
                          {categoryGames.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              No games in this category yet
                            </div>
                          ) : (
                            categoryGames.map(game => {
                              const gameIdStr = (game.gameId || game.id?.toString()).toString();
                              
                              // 해당 게임의 모든 플레이 기록
                              const rawPlayHistory = getFilteredFeedbackData().filter(fb => {
                                if (!fb.gameId) return false;
                                
                                const fbGameId = fb.gameId.toString();
                                
                                // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                                if (fbGameId === gameIdStr) return true;
                                
                                const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                                const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                                
                                return fbNumeric === gameNumeric;
                              });
                              
                              // 이벤트별로 중복 제거
                              const playHistoryMap = new Map();
                              rawPlayHistory.forEach(play => {
                                const key = `${play.eventId}-${play.eventTitle}`;
                                if (!playHistoryMap.has(key) || play.rating > (playHistoryMap.get(key).rating || 0)) {
                                  playHistoryMap.set(key, play);
                                }
                              });
                              const playHistory = Array.from(playHistoryMap.values());
                              
                              if (playHistory.length === 0) {
                                return (
                                  <div key={game.id} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-semibold text-gray-900">{game.name}</div>
                                        <div className="text-sm text-gray-500 mt-1">Not played yet</div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-sm text-gray-500">Team Size: {game.recommendedTeamSize || game.optimalParticipants || game.optimalPlayers || 'N/A'}</div>
                                        <div className="text-sm text-gray-500">Range: {game.totalMinPlayers || game.minParticipants || game.minPlayers}-{game.totalMaxPlayers || game.maxParticipants || game.maxPlayers}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              
                              // 마지막 플레이 날짜
                              const lastPlayed = playHistory.reduce((latest, fb) => {
                                const fbDate = new Date(fb.eventDate);
                                return fbDate > latest ? fbDate : latest;
                              }, new Date(0));
                              
                              return (
                                <div key={game.id} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                                  <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                      <div className="font-semibold text-gray-900 text-lg mb-2">{game.name}</div>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <div className="flex items-center">
                                          <span className="text-gray-500 mr-2">📅</span>
                                          <span className="font-medium">{lastPlayed.toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex items-center">
                                          <span className="text-gray-500 mr-2">🎮</span>
                                          <span className="font-medium">{game.playedCount || 0} times</span>
                                        </div>
                                        <div className="flex items-center">
                                          <span className="text-yellow-500 mr-2">⭐</span>
                                          <span className="font-medium">{game.averageRating ? Number(game.averageRating).toFixed(1) : 'N/A'}</span>
                                          {game.averageRating && <span className="text-gray-500 ml-1">({game.playedCount} votes)</span>}
                                        </div>
                                        <div className="flex items-center">
                                          <span className="text-gray-500 mr-2">👥</span>
                                          <span className="font-medium">{game.recommendedTeamSize || game.optimalParticipants || game.optimalPlayers || 'N/A'}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Play History */}
                                  <div className="mt-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Recent Play History</h5>
                                    <div className="space-y-2">
                                      {playHistory.slice(0, 5).map((play, idx) => (
                                        <div key={idx} className="p-2 bg-gray-50 rounded border border-gray-200 text-xs">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                              <span className="font-medium text-gray-900">{play.eventTitle}</span>
                                              <span className="text-gray-400">{new Date(play.eventDate).toLocaleDateString()}</span>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                              <span className="text-gray-600">👥 {play.willAttendCount || play.attendeeCount || 'N/A'}</span>
                                              <div className="flex items-center">
                                                <span className="text-yellow-500 mr-1">★</span>
                                                <span className="font-medium">{play.rating ? Number(play.rating).toFixed(1) : 'N/A'}</span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      {playHistory.length > 5 && (
                                        <div className="text-xs text-gray-500 italic text-center">
                                          +{playHistory.length - 5} more plays
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}